import { t } from '@lingui/macro'
import { isBefore } from 'date-fns'
import { isEmpty, isNil } from 'ramda'

import { formatAgendaTime } from '@/modules/shared/helpers/agenda-moment'

import type { AgendaDetailTone } from '@/modules/shared/components/agenda-detail/AgendaDetail'
import type { Encounter } from '../types/encounter.types'

type EncounterTimeStatus = 'planned' | 'timeProposed' | 'scheduled' | 'completed'

/**
 * How certain the time of an admission is, which decides what the detail screen shows next to the
 * clock icon:
 *
 * - `completed`: the admission has an end, so the recorded time is shown as is.
 * - `timeProposed`: the hospital proposed a check-in time the patient still has to confirm.
 * - `scheduled`: the hospital confirmed a date, or a surgery is planned; the exact time follows later.
 * - `planned`: nothing special, the start time is shown.
 */
function getEncounterTimeStatus(encounter: Encounter): EncounterTimeStatus {
  // The type promises a string, Synops does not: a missing end is not an end either.
  if (encounter.end) {
    return 'completed'
  }

  if (encounter.meta?.requiresConfirmation) {
    return 'timeProposed'
  }

  const hasConfirmedDate = !isNil(encounter.meta?.confirmedDates) && !isEmpty(encounter.meta?.confirmedDates)
  if (hasConfirmedDate || encounter.trigger.startsWith('operatie_')) {
    return 'scheduled'
  }

  return 'planned'
}

/**
 * Whether the admission is over: its end has passed, or Synops marks it finished. The start day says
 * nothing about that, since a stay runs over several days and the patient needs the meal choice and
 * the contact precisely while it does. An end alone is not enough either: Synops sends the scheduled
 * end of the slot with the booking, long before the stay.
 */
function isFinishedEncounter(encounter: Encounter, now: Date = new Date()): boolean {
  if (encounter.status === 'finished') {
    return true
  }

  return Boolean(encounter.end) && isBefore(new Date(encounter.end), now)
}

/**
 * What the hour row of the admission detail says, and in which tone: the hour itself, the request to
 * keep the day free while the hospital has not set it, or the check-in time to confirm. Nothing when
 * there is no start or the hospital hides the hour.
 */
function getEncounterTimeLabel(encounter: Encounter): { message: string; tone: AgendaDetailTone } | undefined {
  if (!encounter.start || encounter.meta?.hiddenInApp) {
    return undefined
  }

  const startTime = formatAgendaTime(encounter.start)

  switch (getEncounterTimeStatus(encounter)) {
    case 'scheduled': {
      return {
        message: t({
          id: 'encounters.time-unconfirmed',
          message: 'Houd alvast de hele dag vrij; we communiceren het exacte tijdstip later',
        }),
        tone: 'highlight',
      }
    }
    case 'timeProposed': {
      return {
        message: t({ id: 'encounters.check-in-time', message: 'Aanmelden: {time}', values: { time: startTime } }),
        tone: 'warning',
      }
    }
    default: {
      return { message: startTime, tone: 'default' }
    }
  }
}

export type { EncounterTimeStatus }
export { getEncounterTimeLabel, getEncounterTimeStatus, isFinishedEncounter }
